import { generatePath } from 'react-router-dom';
import { useUserStore } from '@Auth/store';
import MODALS from '@constants/modals';
import { useBlockFlags } from '@ContractBuilder/modules/block/context/context';
import { useEntityStore } from '@ContractBuilder/store';
import { useCommentsUIStore } from '@ContractBuilder/store/comments.store';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import { getBlockThreads } from '@features/comments/utils';
import { authProvider } from '@helpers/auth';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useModalDispatch } from '@hooks/useModalProvider';
import Nav from '@src/routes';

import { deriveContractBlockActionsState } from '../utils/derive-contract-block-actions-state';
import type { ContractBlockActionsViewProps } from '../views/ContractBlockActionsView';

export const useContractBlockActions = (
  block: ResourceBlock,
): Omit<ContractBlockActionsViewProps, 'onDelete' | 'onEdit'> => {
  const {
    context,
    details: { isDeletable, isEditable },
  } = useBlockFlags();

  const { user } = useUserStore();
  const getFeatureFlagState = useFeatureFlags();
  const { showModal } = useModalDispatch();
  const submission = useEntityStore(({ submission }) => submission);
  const openThreads = useCommentsUIStore(({ openThreads }) => openThreads);
  const startThread = useCommentsUIStore(({ startThread }) => startThread);

  const { id, blockLibraryId, type, canEditOnTemplate = true } = block;

  const displayState = deriveContractBlockActionsState({
    blockId: id,
    blockType: type,
    canEditOnTemplate,
    context,
    user,
    submission,
    isDeletable,
    isEditable,
    commentsFeatureEnabled: getFeatureFlagState('Comments'),
  });

  const handleOpenVisibilityModal = () => showModal(MODALS.BLOCK_VISIBILITY_MODAL, { blockId: id });
  // Open threads first so the reply composer lands on one the user can still answer.
  const handleCommentClick = () =>
    openThreads(
      id,
      getBlockThreads(id, submission?.comments)
        .sort((a, b) => Number(a.isResolved) - Number(b.isResolved))
        .map((thread) => thread.id),
    );
  const handleAddComment = () => startThread(id);

  const handleViewInLibrary = async () => {
    const searchParams = await authProvider.getAuthSearchParams();
    const to = generatePath(displayState.isClause ? Nav.Clause : Nav.Block, { id: blockLibraryId ?? '' });
    window.open(`${to}${searchParams}`);
  };

  return {
    block,
    ...displayState,
    onCommentClick: handleCommentClick,
    onAddComment: handleAddComment,
    onViewInLibrary: handleViewInLibrary,
    onOpenVisibilityModal: handleOpenVisibilityModal,
  };
};
